import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { useAuth } from '../context/AuthContext';
import ProductEditTable from './ProductEditTable';

/**
 * InvoiceProductImportDialog — "Import New" (temporaire)
 *
 * Ajout de produits au catalogue depuis une facture/devis PDF.
 * Pipeline : PDF natif -> Claude vision -> validation arithmétique -> relecture -> import.
 * Règle : 1 facture = 1 fournisseur. Les quantités facturées ne sont PAS importées.
 */
export const InvoiceProductImportDialog = ({ open, onOpenChange, suppliers, onImportSuccess }) => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | review
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [docInfo, setDocInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [excludedLines, setExcludedLines] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [rayonOptions, setRayonOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [packagingTypeOptions, setPackagingTypeOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkRayon, setBulkRayon] = useState('');

  useEffect(() => {
    if (open && !typeOptions.length) {
      Promise.all([api.get('/types'), api.get('/rayons'), api.get('/packaging-options')])
        .then(([t, r, p]) => {
          setTypeOptions(t.data || []);
          setRayonOptions(r.data || []);
          setUnitOptions(p.data?.units || []);
          setPackagingTypeOptions(p.data?.types || []);
        })
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setStep('upload');
      setFile(null);
      setDocInfo(null);
      setProducts([]);
      setExcludedLines([]);
      setSupplierId('');
      setUpdateExisting(false);
      setSelectedIds(new Set());
      setBulkType('');
      setBulkRayon('');
    }
  }, [open]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    setFile(f || null);
  };

  const guessSupplier = (detectedName) => {
    if (!detectedName || !suppliers?.length) return '';
    const detected = detectedName.toUpperCase();
    const match = suppliers.find(s => {
      const name = (s.name || '').toUpperCase();
      return name && (detected.includes(name) || name.includes(detected.split(' ')[0]));
    });
    return match?.id || '';
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const startResponse = await api.post('/products/extract-from-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      const { job_id } = startResponse.data;

      // Polling du job (l'extraction peut prendre 1-2 min sur les grosses factures)
      const maxAttempts = 100; // ~5 min
      let job = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data } = await api.get(`/products/extract-from-invoice/${job_id}`);
        if (data.status === 'completed') { job = data; break; }
        if (data.status === 'failed') {
          throw new Error(data.error || 'Extraction échouée');
        }
      }
      if (!job) {
        throw new Error("L'analyse a pris trop de temps, veuillez réessayer");
      }

      const { document: doc, products: extracted, excluded_lines, stats } = job.result;

      if (!extracted.length) {
        toast.error('Aucun produit détecté sur ce document');
        return;
      }

      setDocInfo(doc);
      setExcludedLines(excluded_lines || []);
      setProducts(extracted.map((p, idx) => ({
        id: `ocr-${idx}`,
        code: p.code || '',
        article: p.article || '',
        product_type: p.product_type || '',
        rayon: p.rayon || '',
        dlc: Boolean(p.dlc),
        supplier_packaging_quantity: p.supplier_packaging_quantity ?? '',
        supplier_packaging_unit: p.supplier_packaging_unit || '',
        supplier_packaging_type: p.supplier_packaging_type || 'colis',
        supplier_package_price: p.supplier_package_price ?? '',
        is_divisible: Boolean(p.is_divisible),
        divisible_quantity: p.divisible_quantity ?? '',
        divisible_unit: p.divisible_unit || '',
        catalog_price: p.catalog_price ?? '',
        _ocr: p._ocr,
      })));
      setSupplierId(guessSupplier(doc?.fournisseur_detecte));
      setStep('review');
      toast.success(`${stats.total_produits} produit(s) extrait(s) — ${stats.verifies} vérifié(s) ✓`);
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(formatErrorMessage(error, "Erreur lors de l'analyse de la facture"));
    } finally {
      setAnalyzing(false);
    }
  };

  const updateProduct = (id, field, value) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, [field]: value };
      // Auto-calcul du prix catalogue (même convention que l'import standard)
      if (['supplier_packaging_quantity', 'supplier_package_price', 'is_divisible', 'divisible_quantity'].includes(field)) {
        const qty = parseFloat(updated.supplier_packaging_quantity) || 0;
        const price = parseFloat(updated.supplier_package_price) || 0;
        const divQty = parseFloat(updated.divisible_quantity) || 1;
        if (updated.is_divisible && qty > 0 && price > 0 && divQty > 0) {
          updated.catalog_price = (price / qty * divQty).toFixed(2);
        } else if (price > 0) {
          updated.catalog_price = price.toFixed(2);
        }
      }
      return updated;
    }));
  };

  const removeProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(products.map(p => p.id)) : new Set());
  };

  const applyBulk = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un produit (cases à cocher)');
      return;
    }
    if (!bulkType && !bulkRayon) {
      toast.error('Choisissez un type et/ou un rayon à appliquer');
      return;
    }
    setProducts(prev => prev.map(p => {
      if (!selectedIds.has(p.id)) return p;
      return {
        ...p,
        ...(bulkType ? { product_type: bulkType } : {}),
        ...(bulkRayon ? { rayon: bulkRayon } : {}),
      };
    }));
    toast.success(`${selectedIds.size} produit(s) mis à jour`);
  };

  const handleImport = async () => {
    if (!supplierId) {
      toast.error('Veuillez sélectionner un fournisseur (1 facture = 1 fournisseur)');
      return;
    }
    const valid = products.filter(p => p.code && p.article);
    if (!valid.length) {
      toast.error('Aucun produit valide (code + article requis)');
      return;
    }
    setImporting(true);
    try {
      const response = await api.post('/products/import', {
        supplier_id: supplierId,
        products: valid.map(p => ({
          code: p.code,
          article: p.article,
          product_type: p.product_type || '',
          rayon: p.rayon || '',
          dlc: Boolean(p.dlc),
          supplier_packaging_quantity: p.supplier_packaging_quantity !== '' && p.supplier_packaging_quantity != null ? parseFloat(p.supplier_packaging_quantity) : null,
          supplier_packaging_unit: p.supplier_packaging_unit || null,
          supplier_packaging_type: p.supplier_packaging_type || null,
          supplier_package_price: p.supplier_package_price !== '' && p.supplier_package_price != null ? parseFloat(p.supplier_package_price) : null,
          is_divisible: Boolean(p.is_divisible),
          divisible_quantity: p.divisible_quantity !== '' && p.divisible_quantity != null ? parseFloat(p.divisible_quantity) : null,
          divisible_unit: p.divisible_unit || null,
          catalog_price: p.catalog_price !== '' && p.catalog_price != null ? parseFloat(p.catalog_price) : null,
          catalog_unit: p.is_divisible ? (p.divisible_unit || null) : null,
          unit_price: p.catalog_price !== '' && p.catalog_price != null ? parseFloat(p.catalog_price) : null,
        })),
        update_existing: updateExisting,
      });
      const { created, updated, skipped, errors } = response.data;
      toast.success(`✅ ${created} créé(s), ${updated} mis à jour, ${skipped} ignoré(s)`);
      if (errors?.length) {
        errors.slice(0, 3).forEach(e => toast.warning(e));
      }
      onImportSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(formatErrorMessage(error, "Erreur lors de l'import"));
    } finally {
      setImporting(false);
    }
  };

  const nbVerified = products.filter(p => p._ocr?.verifie).length;
  const nbToCheck = products.length - nbVerified;

  const handleOpenChange = (value) => {
    if (!value && step === 'review' && products.length > 0 && !importing) {
      if (!window.confirm('Fermer sans importer ? Les données extraites seront perdues.')) return;
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={step === 'review' ? 'max-w-[95vw] w-[1400px] max-h-[92vh] overflow-hidden flex flex-col' : 'max-w-lg'}
        data-testid="invoice-product-import-dialog"
        onInteractOutside={(e) => { if (step === 'review') e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            Import New — Ajout de produits depuis une facture
          </DialogTitle>
          <DialogDescription>
            {step === 'upload'
              ? 'Uploadez une facture ou un devis PDF. 1 facture = 1 fournisseur.'
              : 'Vérifiez les produits extraits puis associez le fournisseur. Les quantités facturées ne sont pas importées.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-2">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="invoice-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={handleFileChange}
                data-testid="invoice-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-emerald-700">
                  <FileText className="w-6 h-6" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(0)} Ko)</span>
                </div>
              ) : (
                <div className="text-gray-500">
                  <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                  <p className="font-medium">Cliquez pour sélectionner un PDF</p>
                  <p className="text-sm mt-1">Facture ou devis fournisseur (max 20 Mo)</p>
                </div>
              )}
            </div>

            <Button
              className="w-full bg-[#1B4332] hover:bg-[#0D2E1F]"
              disabled={!file || analyzing}
              onClick={handleAnalyze}
              data-testid="analyze-invoice-button"
            >
              {analyzing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours (10-60s)...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Analyser la facture</>
              )}
            </Button>
          </div>
        )}

        {step === 'review' && (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Infos document + validation globale */}
            <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border text-sm">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{docInfo?.fournisseur_detecte || 'Fournisseur inconnu'}</span>
              {docInfo?.numero_document && <Badge variant="outline">N° {docInfo.numero_document}</Badge>}
              {docInfo?.total_ht_document != null && (
                <Badge variant="outline">Total HT : {Number(docInfo.total_ht_document).toFixed(2)} €</Badge>
              )}
              {docInfo?.total_coherent ? (
                <Badge className="bg-green-100 text-green-700 border-green-300 hover:bg-green-100" data-testid="total-coherent-badge">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Totaux cohérents
                </Badge>
              ) : docInfo?.total_coherent === false && docInfo?.total_ht_document != null ? (
                <Badge className="bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-100" data-testid="total-incoherent-badge">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Écart somme lignes / total ({docInfo?.somme_lignes ?? '?'} €)
                </Badge>
              ) : (
                <Badge variant="outline" data-testid="no-total-badge">Listing / devis sans total</Badge>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700 border-green-300">{nbVerified} vérifié(s) ✓</Badge>
                {nbToCheck > 0 && (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-300">{nbToCheck} à contrôler ⚠</Badge>
                )}
              </div>
            </div>

            {/* Fournisseur — obligatoire */}
            <div className="flex flex-wrap items-end gap-4 p-3 border rounded-lg bg-white">
              <div className="flex-1 min-w-[260px]">
                <Label className="text-sm font-semibold">
                  Fournisseur associé * <span className="font-normal text-gray-500">(1 facture = 1 fournisseur)</span>
                </Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger className="mt-1" data-testid="supplier-select">
                    <SelectValue placeholder="Sélectionner le fournisseur..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers?.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {docInfo?.fournisseur_detecte && (
                  <p className="text-xs text-gray-500 mt-1">
                    Détecté sur la facture : <strong>{docInfo.fournisseur_detecte}</strong>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 pb-1">
                <Switch
                  checked={updateExisting}
                  onCheckedChange={setUpdateExisting}
                  data-testid="update-existing-switch"
                />
                <Label className="text-sm">Mettre à jour les produits existants (même code)</Label>
              </div>
            </div>

            {/* Lignes exclues (frais, consignes) */}
            {excludedLines.length > 0 && (
              <Alert className="py-2">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription className="text-xs">
                  <strong>{excludedLines.length} ligne(s) exclue(s)</strong> (frais/consignes, non importées) :{' '}
                  {excludedLines.map(l => `${l.designation}${l.prix_total_ligne != null ? ` (${Number(l.prix_total_ligne).toFixed(2)}€)` : ''}`).join(' • ')}
                </AlertDescription>
              </Alert>
            )}

            {/* Actions groupées : type / rayon sur la sélection */}
            <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-emerald-50/50 border-emerald-200" data-testid="bulk-actions-bar">
              <div className="text-sm font-semibold text-gray-800 pb-2">
                Actions groupées
                <span className="font-normal text-gray-500"> — {selectedIds.size} sélectionné(s)</span>
              </div>
              <div className="min-w-[170px]">
                <Label className="text-xs text-gray-600">Type de produit</Label>
                <Select value={bulkType || undefined} onValueChange={setBulkType}>
                  <SelectTrigger className="h-9 bg-white" data-testid="bulk-type-select">
                    <SelectValue placeholder="Type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[190px]">
                <Label className="text-xs text-gray-600">Rayon</Label>
                <Select value={bulkRayon || undefined} onValueChange={setBulkRayon}>
                  <SelectTrigger className="h-9 bg-white" data-testid="bulk-rayon-select">
                    <SelectValue placeholder="Rayon..." />
                  </SelectTrigger>
                  <SelectContent>
                    {rayonOptions.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-9 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={applyBulk}
                disabled={selectedIds.size === 0 || (!bulkType && !bulkRayon)}
                data-testid="apply-bulk-button"
              >
                Appliquer à la sélection
              </Button>
            </div>

            {/* Tableau d'édition — même formulaire que l'import standard */}
            <ProductEditTable
              products={products}
              onProductUpdate={updateProduct}
              onProductRemove={removeProduct}
              minProducts={1}
              showVerification
              showDlc
              typeOptions={typeOptions}
              rayonOptions={rayonOptions}
              unitOptions={unitOptions}
              packagingTypeOptions={packagingTypeOptions}
              selectable
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
            />
          </div>
        )}

        {step === 'review' && (
          <div className="border-t pt-3 flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep('upload')} disabled={importing}>
              ← Changer de fichier
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {products.filter(p => p.code && p.article).length} produit(s) prêt(s)
              </span>
              <Button
                className="bg-[#1B4332] hover:bg-[#0D2E1F]"
                onClick={handleImport}
                disabled={importing || !products.length}
                data-testid="confirm-import-button"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import...</>
                ) : (
                  <>Importer {products.filter(p => p.code && p.article).length} produit(s)</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceProductImportDialog;
